/**
 * @package   EGI / Frontend — wishlist
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.1 (M-EGI-373 — remediation P2-1)
 * @date      2026-07-03
 * @purpose   ServerDriver autenticato: chiamate alle API wishlist reali
 *            (WishlistController — shape verificate alla fonte: toggle/remove
 *            → {success,is_in_wishlist,wishlist_count}, index → {items,total,…},
 *            merge → {merged,skipped,wishlist_count}). Pattern likeService:
 *            X-CSRF-TOKEN + credentials same-origin + X-Requested-With +
 *            AbortController per-risorsa (toggle ravvicinati → abort precedente).
 *            Errori → uem-report (codici WISHLIST_* — inclusa la lista via
 *            WISHLIST_LIST_FAILED; console come fallback se UEM non pronto).
 */

import { getCsrfTokenTS } from '../utils/csrf';
import { reportClientError, reportServerError } from './uem-report';
import type { WishlistEndpoints, WishlistItem } from './types';
import type { MergePayloadRow, MergeTransportResult } from './merge';

export function isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
}

function asRecord(value: unknown): Record<string, unknown> {
    if (typeof value !== 'object' || value === null) {
        throw new Error('Wishlist API: unexpected response shape');
    }
    return value as Record<string, unknown>; // narrow post-guard: lettura chiavi tipizzata sotto
}

function toCount(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

export interface ServerListResult {
    items: WishlistItem[];
    total: number;
}

export class WishlistServerDriver {
    private readonly aborts = new Map<string, AbortController>();

    public constructor(private readonly endpoints: WishlistEndpoints) {}

    public async toggle(id: string): Promise<{ inWishlist: boolean; count: number }> {
        const data = await this.request(
            this.urlFor(this.endpoints.toggle, id),
            { method: 'POST', signal: this.takeAbortSignal('egi-' + id) },
            'WISHLIST_TOGGLE_FAILED',
            { egi_id: id, action: 'toggle' },
        );
        const record = asRecord(data);
        return { inWishlist: record.is_in_wishlist === true, count: toCount(record.wishlist_count) };
    }

    public async remove(id: string): Promise<{ count: number }> {
        const data = await this.request(
            this.urlFor(this.endpoints.remove, id),
            { method: 'DELETE', signal: this.takeAbortSignal('egi-' + id) },
            'WISHLIST_TOGGLE_FAILED', // stesso codice usato dal backend per action=remove
            { egi_id: id, action: 'remove' },
        );
        return { count: toCount(asRecord(data).wishlist_count) };
    }

    /** Lista canonica — cap server 50 → una pagina per_page=50 copre tutto. */
    public async fetchList(): Promise<ServerListResult> {
        const data = await this.request(
            this.endpoints.list + '?per_page=50',
            { method: 'GET' },
            'WISHLIST_LIST_FAILED', // registrazione backend in parallelo (M-EGI-373 P2-1); fallback console se UEM non pronto
            { action: 'list' },
        );
        const record = asRecord(data);
        const rows = Array.isArray(record.items) ? record.items : [];
        const items: WishlistItem[] = [];
        for (const raw of rows) {
            const row = asRecord(raw);
            if (typeof row.egi_id !== 'number') {
                continue;
            }
            const parsedAt = typeof row.added_at === 'string' ? Date.parse(row.added_at) : Number.NaN;
            items.push({
                id: String(row.egi_id),
                title: typeof row.title === 'string' ? row.title : '',
                imageUrl: typeof row.image_url === 'string' ? row.image_url : '',
                addedAt: Number.isFinite(parsedAt) ? parsedAt : Date.now(),
            });
        }
        return { items, total: toCount(record.total) };
    }

    public async merge(items: ReadonlyArray<MergePayloadRow>): Promise<MergeTransportResult> {
        const data = await this.request(
            this.endpoints.merge,
            { method: 'POST', body: JSON.stringify({ items }) },
            'WISHLIST_MERGE_FAILED',
            { items_count: items.length },
        );
        const record = asRecord(data);
        const skipped: Array<{ id: string; reason: string }> = [];
        if (Array.isArray(record.skipped)) {
            for (const raw of record.skipped) {
                const row = asRecord(raw);
                if (typeof row.id === 'string') {
                    skipped.push({ id: row.id, reason: typeof row.reason === 'string' ? row.reason : '' });
                }
            }
        }
        return {
            merged: toCount(record.merged),
            skipped,
            count: toCount(record.wishlist_count),
        };
    }

    private urlFor(template: string, id: string): string {
        return template.replace('__ID__', encodeURIComponent(id));
    }

    /** Aborta l'eventuale richiesta in volo sulla stessa risorsa (pattern likeService). */
    private takeAbortSignal(key: string): AbortSignal {
        this.aborts.get(key)?.abort();
        const controller = new AbortController();
        this.aborts.set(key, controller);
        return controller.signal;
    }

    private async request(
        url: string,
        init: RequestInit,
        errorCode: string | null,
        context: Record<string, unknown>,
    ): Promise<unknown> {
        let response: Response;
        try {
            response = await fetch(url, {
                ...init,
                credentials: 'same-origin', // ESSENZIALE: sessione FegiAuth via cookie
                headers: {
                    'X-CSRF-TOKEN': getCsrfTokenTS(),
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });
        } catch (error) {
            if (isAbortError(error)) {
                throw error; // toggle superato da uno più recente: non è un errore
            }
            reportClientError(
                errorCode,
                { ...context, error: error instanceof Error ? error.message : String(error) },
                error instanceof Error ? error : undefined,
            );
            throw error;
        }

        if (!response.ok) {
            const errorData: unknown = await response.json().catch(() => null);
            reportServerError(errorCode, errorData, { ...context, status: response.status });
            throw new Error('Wishlist API: HTTP ' + String(response.status));
        }

        return (await response.json()) as unknown;
    }
}
